import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { Box, CircularProgress } from "@mui/material";
import { isEmpty } from "lodash-es";
import { useEffect, useState } from "react";
import { EDITOR_THEME } from "../message/editor/theme/theme";
import { loadDesign } from "../message/editor/utils/load-design";
import { EDITOR_NODES } from "../message/editor/utils/nodes";
import { tableNodeReplacement } from "../message/editor/utils/use-styleable-columns";

import type { SerialisedEditorDesign } from "../message/editor/types/serialised-editor-design";
import type { SxProps } from "@mui/material";
import type {
  EditorThemeClasses,
  HTMLConfig,
  InitialEditorStateType,
  Klass,
  LexicalEditor,
  LexicalNode,
  LexicalNodeReplacement,
} from "lexical";
import type { FC } from "react";

export const ReadOnlyEditor: FC<{
  design: SerialisedEditorDesign;
  format: "email" | "chat" | "sms";
  sx?: SxProps;
  fullWidth?: boolean;
}> = ({ design, format, sx, fullWidth }) => {
  const [initialConfig, setInitialConfig] = useState<Readonly<{
    namespace: string;
    nodes?: readonly (Klass<LexicalNode> | LexicalNodeReplacement)[];
    onError: (error: Error, editor: LexicalEditor) => void;
    onWarn?: (error: Error, editor: LexicalEditor) => void;
    editable?: boolean;
    theme?: EditorThemeClasses;
    editorState?: InitialEditorStateType;
    html?: HTMLConfig;
  }> | null>(null);

  useEffect(() => {
    loadDesign(design, format)
      .then((editorState) =>
        setInitialConfig({
          editorState,
          editable: false,
          namespace: "ce",
          onError: console.error,
          nodes: tableNodeReplacement(EDITOR_NODES[format]),
          theme: EDITOR_THEME,
        }),
      )
      .catch(console.error);
  }, [design, format]);

  if (isEmpty(design)) {
    return null;
  }
  const headerAndEditor = (
    <Box
      sx={{
        ...sx,
        margin: "0px auto",
      }}
    >
      <RichTextPlugin
        placeholder={() => null}
        ErrorBoundary={LexicalErrorBoundary}
        contentEditable={
          <ContentEditable className="editor-content read-only" data-testid="read-only-editor" />
        }
      />
    </Box>
  );

  const wrapper = fullWidth ? (
    <Box sx={{ ...sx, width: "100%", p: 0, m: 0 }}>{headerAndEditor}</Box>
  ) : (
    headerAndEditor
  );

  if (!initialConfig) return <CircularProgress />;
  return (
    <LexicalComposer key={JSON.stringify(design)} initialConfig={initialConfig}>
      {wrapper}
    </LexicalComposer>
  );
};
